var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { READ_CACHE_POLICIES } from '../../api/constants';
import { getParamsId } from '../../api/lib';
import { ApiContext } from '../context';
import { useApiQueryActions } from './actions';
import { useApiQueryReducer } from './reducer';
/**
 * API hook to run an api `GET` request, returning a `Loadable`
 * instance of the response data
 */
export function useApiQuery(params, opts = {}) {
    const { api, defaultFetchPolicy } = useContext(ApiContext);
    const fetchPolicy = opts.fetchPolicy || defaultFetchPolicy;
    const paramsId = params && getParamsId(params);
    const [state, dispatch] = useReducer(useApiQueryReducer, null, () => ({
        id: null,
        paramsId: null,
        loading: !!params,
        data: 
        // if this api request will read from the cache,
        // initialize data to the value in the cache
        params && READ_CACHE_POLICIES.includes(fetchPolicy)
            ? api.readCachedResponse(params) || null
            : null,
        error: null
    }));
    useEffect(() => {
        if (!params) {
            dispatch(useApiQueryActions.reset());
            return undefined;
        }
        const unsubscribe = api.onCacheUpdate(params, (responseBody) => {
            dispatch(useApiQueryActions.setData(responseBody));
        });
        const cachedData = READ_CACHE_POLICIES.includes(fetchPolicy)
            ? api.readCachedResponse(params)
            : null;
        if (fetchPolicy === 'cache-only' && cachedData) {
            // don't bother kicking off a request
            // since we already have the cached data
            useApiQueryActions.setData(cachedData);
            return undefined;
        }
        const id = Symbol();
        dispatch(useApiQueryActions.request({
            id,
            paramsId: paramsId,
            dontReinitialize: opts.dontReinitialize,
            initData: cachedData || null
        }));
        (() => __awaiter(this, void 0, void 0, function* () {
            try {
                const data = yield api.request(params, {
                    fetchPolicy,
                    deduplicate: opts.deduplicate
                });
                dispatch(useApiQueryActions.success({
                    id,
                    paramsId: paramsId,
                    data
                }));
            }
            catch (error) {
                dispatch(useApiQueryActions.failure({
                    id,
                    paramsId: paramsId,
                    error
                }));
            }
        }))();
        return () => {
            unsubscribe();
        };
    }, [paramsId]);
    // return loading flag if `useEffect` hasn't kicked in yet
    // but the a new request is about to kick off
    const prevParamsId = usePrevious(paramsId);
    const aboutToStartNewRequest = paramsId && paramsId !== prevParamsId;
    const returnedState = useMemo(() => ({
        loading: aboutToStartNewRequest || state.loading,
        data: state.data,
        error: state.error
    }), [aboutToStartNewRequest, state.data, state.loading, state.error]);
    return [
        returnedState,
        {
            setData: (data) => {
                if (fetchPolicy === 'no-cache' || !params) {
                    dispatch(useApiQueryActions.setData(data));
                }
                else {
                    // write to the cache, which will in turn
                    // notify the subscription and update `state.data`
                    api.writeCachedResponse(params, data);
                }
            },
            refetch: (refetchOpts = {}) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (!paramsId || !params) {
                    return;
                }
                const id = Symbol();
                dispatch(useApiQueryActions.refetchRequest({
                    id,
                    paramsId,
                    reinitialize: refetchOpts.reinitialize
                }));
                try {
                    const data = yield api.request(params, {
                        fetchPolicy: fetchPolicy === 'no-cache' ? 'no-cache' : 'fetch-first',
                        deduplicate: (_a = refetchOpts.deduplicate, (_a !== null && _a !== void 0 ? _a : opts.deduplicate))
                    });
                    dispatch(useApiQueryActions.success({ id, paramsId, data }));
                }
                catch (error) {
                    dispatch(useApiQueryActions.failure({ id, paramsId, error }));
                }
            })
        }
    ];
}
/**
 * Returns previous value, or null if first render pass
 * @param value updating value
 */
function usePrevious(value) {
    const ref = useRef(null);
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}
//# sourceMappingURL=index.js.map